import { Injectable, Injector } from '@angular/core';
import {
  AbstractControlOptions,
  UntypedFormArray,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { PrFormAbstract } from '@app/components/pr-form/pr-form.abstract';
import { PrFormConstructor } from '@app/components/pr-form/pr-form.contructor';
import { UserDao } from '@app/dao/user/user.dao';
import {
  DependentChildren,
  HouseholdChild,
} from '@app/forms/subscription/definition/interface/subscription';
import { FormValidators } from '@app/providers/utils/form-validators';
import * as _ from 'lodash';
import { take } from 'rxjs';
import FormGroupArray from '../../services/FormGroupArray';
@Injectable({
  providedIn: 'root',
})
export abstract class EaSubscriptionBuilder extends PrFormAbstract {
  userId;
  constructor(
    protected formConstructor: PrFormConstructor,
    protected injector: Injector,
  ) {
    super(formConstructor);
    injector
      .get(UserDao)
      .getAuthentified()
      .pipe(take(1))
      .subscribe(user => {
        this.userId = user.id;
      });
  }

  private removedChildren = new WeakMap<
    UntypedFormGroup,
    UntypedFormGroup[]
  >();

  generateForm(entity: DependentChildren) {
    let options: AbstractControlOptions = {};
    const children = entity?.householdChildren || [];

    const owner = this.formBuilder.group(
      {
        ownerId: [entity?.ownerId],
        hasChild: [children.length > 0, Validators.required],
        childrenCount: [children.length],
        householdChildren: this.formBuilder.array(
          children.map(child => this.createChildFormGroup(child)),
        ),
        initialRelationIds: [
          children.map(child => child.relationId).filter(Boolean),
        ],
        pendingDeletedRelationIds: [entity?.deletedChildRelationIds || []],
      },
      options,
    );

    owner.get('childrenCount').valueChanges.subscribe(count => {
      if (!_.isNil(count)) {
        this.adjustChildren(owner, count);
      }
    });

    return owner;
  }

  createChildFormGroup(child?: HouseholdChild) {
    return this.formBuilder.group({
      relationId: [child?.relationId || null],
      userId: [child?.userId || null],
      firstName: [child?.firstName || ''],
      origin: [child?.childFiliation || ''],
      birthDate: [child?.birthDate || '', FormValidators.dateNotInFuture],
      dependant: [child?.inCharge || false],
      handicap: [child?.handicapped || false],
    });
  }

  loadChildren(owner: UntypedFormGroup, children: HouseholdChild[]) {
    const array = this.householdChildren(owner);
    array.clear();
    children.forEach(child => array.push(this.createChildFormGroup(child)));
    owner.get('childrenCount').setValue(children.length, { emitEvent: false });
    owner.get('hasChild').setValue(children.length > 0, { emitEvent: false });
    owner
      .get('initialRelationIds')
      .setValue(children.map(child => child.relationId).filter(Boolean));
  }

  toHouseholdChild(relation: any): HouseholdChild {
    return {
      relationId: relation?.id,
      userId: relation?.relationUser?.id,
      firstName: relation?.relationUser?.firstName,
      birthDate: relation?.relationUser?.birthDate,
      childFiliation: relation?.childFiliation,
      inCharge: relation?.inCharge,
      handicapped: relation?.relationUser?.isHandicapped,
    };
  }

  householdChildren(owner: UntypedFormGroup): UntypedFormArray {
    return owner.get('householdChildren') as UntypedFormArray;
  }

  removeChild(owner: UntypedFormGroup, index: number) {
    this.householdChildren(owner).removeAt(index);
    owner
      .get('childrenCount')
      .setValue(this.householdChildren(owner).length, { emitEvent: false });
  }

  private adjustChildren(owner: UntypedFormGroup, targetCount: number) {
    const children = this.householdChildren(owner);
    const removed = this.removedChildren.get(owner) || [];
    while (children.length < targetCount) {
      children.push(removed.pop() || this.createChildFormGroup());
    }
    while (children.length > targetCount) {
      removed.push(children.at(children.length - 1) as UntypedFormGroup);
      children.removeAt(children.length - 1);
    }
    this.removedChildren.set(owner, removed);
  }

  private deletedRelationIds(value: any): number[] {
    const currentIds = (value.householdChildren || [])
      .map((child: any) => child.relationId)
      .filter(Boolean);
    const removedIds = (value.initialRelationIds || []).filter(
      (relationId: number) => !currentIds.includes(relationId),
    );
    return _.uniq([...(value.pendingDeletedRelationIds || []), ...removedIds]);
  }

  generate(entity: DependentChildren[], option) {
    return FormGroupArray.generate({
      entity,
      options: option,
      formBuilder: this.formBuilder,
      generateForm: this.generateForm.bind(this),
      ownerId: this.userId,
    });
  }
  get owners() {
    return this.form.get('owners') as UntypedFormArray;
  }
  extract() {
    const formValues: any[] = _.cloneDeep(this.form.value.owners);

    return formValues?.map((value: any) => ({
      ownerId: value.ownerId,
      householdChildren: (value.householdChildren || []).map(
        (child: any): HouseholdChild => ({
          relationId: child.relationId,
          userId: child.userId,
          firstName: child.firstName || '',
          birthDate: this.emptyIsNull(child.birthDate),
          childFiliation: this.emptyIsNull(child.origin),
          inCharge: child.dependant || false,
          handicapped: child.handicap || false,
        }),
      ),
      deletedChildRelationIds: this.deletedRelationIds(value),
    }));
  }
}
